/** Success marker of the backend envelope. */
export const API_OK = "ok";

export const API_ERROR_CODES = [
  "validation_error",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "plan_required",
  "budget_exceeded",
  "rate_limited",
  "tenant_context_missing",
  "external_service_error",
  "internal_error",
  "network_error",
  "unknown",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export interface ApiErrorOptions {
  status: number;
  code: string;
  message: string;
  requestId?: string | null;
  details?: unknown;
}

/**
 * Normalised error for every failed request.
 * Read `status` first (HTTP semantics), then `code` (stable, maps to i18n keys).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  /** Payload of `data`: validation issues for 422, null otherwise. */
  readonly details: unknown;

  constructor(options: ApiErrorOptions) {
    super(options.message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.requestId = options.requestId ?? null;
    this.details = options.details ?? null;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === "unauthorized";
  }

  /** Pro feature, but the tenant is not on a paid/effective Pro plan. */
  get isPlanRequired(): boolean {
    return this.status === 402 || this.code === "plan_required";
  }

  get isForbidden(): boolean {
    return this.status === 403 || this.code === "forbidden";
  }

  get isNotFound(): boolean {
    return this.status === 404 || this.code === "not_found";
  }

  get isConflict(): boolean {
    return this.status === 409 || this.code === "conflict";
  }

  get isValidation(): boolean {
    return this.status === 422 || this.code === "validation_error";
  }

  get isNetwork(): boolean {
    return this.status === 0 || this.code === "network_error";
  }

  /** i18n key: `errors.<code>` with a fallback chain handled by the caller. */
  get messageKey(): string {
    return `errors.${this.code}`;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

const STATUS_FALLBACK: Record<number, ApiErrorCode> = {
  400: "validation_error",
  401: "unauthorized",
  402: "plan_required",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  422: "validation_error",
  429: "rate_limited",
  500: "internal_error",
  502: "external_service_error",
  503: "external_service_error",
};

export function codeFromStatus(status: number): ApiErrorCode {
  return STATUS_FALLBACK[status] ?? (status >= 500 ? "internal_error" : "unknown");
}

/** Extract a human-readable message from anything that was thrown. */
export function errorMessage(error: unknown, fallback = "Request failed"): string {
  if (isApiError(error)) return error.message || fallback;
  if (error instanceof Error) return error.message || fallback;
  return fallback;
}
